// src/components/group/GroupInfoSection.js
import React from 'react';
import { useTranslation } from 'react-i18next';

function GroupInfoSection({ group, basicSettings, setBasicSettings, onUpdate, isOpen, onToggle }) {
  const { t } = useTranslation();
  const isDraft = group.status === 'draft';
  
  const isNewGroup = (name) => {
    const newGroupNames = ['Ny grupp', 'New Group', 'Nouvelle groupe', 'Neue Gruppe', 'Nuevo grupo'];
    return newGroupNames.includes(name);
  };
  
  const canEditName = isNewGroup(group.groupName);
  const canChangeMeetingType = isNewGroup(group.groupName);
  
  const sectionTitle = (isDraft && (!group.groupName || isNewGroup(group.groupName))) 
    ? t('groupInfo.newGroup') 
    : t('groupInfo.groupTitle', { name: group.groupName });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(basicSettings);
  };

  // Weekday options
  const weekdayOptions = [
    { value: '', label: t('groupInfo.selectDay') },
    { value: 'Måndag', label: t('groupInfo.weekdays.monday') },
    { value: 'Tisdag', label: t('groupInfo.weekdays.tuesday') },
    { value: 'Onsdag', label: t('groupInfo.weekdays.wednesday') },
    { value: 'Torsdag', label: t('groupInfo.weekdays.thursday') },
    { value: 'Fredag', label: t('groupInfo.weekdays.friday') },
    { value: 'Lördag', label: t('groupInfo.weekdays.saturday') },
    { value: 'Söndag', label: t('groupInfo.weekdays.sunday') }
  ];

  // Kombinerad mötestyp
  const getCombinedMeetingType = () => {
    if (basicSettings.meetingType === 'single') return 'single';
    if (basicSettings.frequency === 'Varannan vecka') return 'biweekly';
    return 'weekly';
  };

  const handleMeetingTypeChange = (combinedType) => {
    if (combinedType === 'single') {
      setBasicSettings(prev => ({ ...prev, meetingType: 'single', frequency: null }));
    } else if (combinedType === 'biweekly') {
      setBasicSettings(prev => ({ ...prev, meetingType: 'recurring', frequency: 'Varannan vecka' }));
    } else {
      setBasicSettings(prev => ({ ...prev, meetingType: 'recurring', frequency: 'Varje vecka' }));
    }
  };

  const combinedMeetingType = getCombinedMeetingType();
  const isSingleMeeting = combinedMeetingType === 'single';

  return (
    <section className="settings-section">
      <div className="section-header" onClick={onToggle}>
        <h2>📋 {sectionTitle}</h2>
        <span className="toggle-icon">{isOpen ? '▼' : '▶'}</span>
      </div>
      {isOpen && (
        <div>
          <form onSubmit={handleSubmit} className="settings-form" style={{ paddingTop: 0 }}>
            
            <div className="required-fields-zone">
              
              {!isDraft && (
                <div className="warning-box" style={{ marginBottom: '20px' }}>
                  {t('groupInfo.activeWarning')}
                </div>
              )}

              {/* Intro-text för nya grupper */}
              {isDraft && (
                <p style={{ 
                  margin: '0 0 20px 0', 
                  fontSize: '16px', 
                  color: '#777',
                  lineHeight: '1.5'
                }}>
                  {t('groupInfo.introText')}
                </p>
              )}

              {/* Row 1: Gruppnamn | Mötestyp | Dag/Datum */}
              <div className="form-row-three">
                {/* 1. GRUPPNAMN */}
                <div className="form-group">
                  <label>
                    {t('groupInfo.groupName')} *
                    {!canEditName && <span style={{ marginLeft: '6px', fontSize: '12px' }}>🔒</span>}
                  </label>
                  <input
                    type="text"
                    value={basicSettings.groupName}
                    onChange={(e) => setBasicSettings(prev => ({...prev, groupName: e.target.value}))}
                    disabled={!canEditName}
                    required
                    placeholder={t('groupInfo.groupNamePlaceholder')}
                  />
                </div>

                {/* 2. MÖTESTYP */}
                <div className="form-group">
                  <label>
                    {t('groupInfo.meetingTypeLabel')} *
                    {!canChangeMeetingType && <span style={{ marginLeft: '6px', fontSize: '12px' }}>🔒</span>}
                  </label>
                  <select
                    value={combinedMeetingType}
                    onChange={(e) => handleMeetingTypeChange(e.target.value)}
                    disabled={!canChangeMeetingType}
                    required
                  >
                    <option value="weekly">{t('groupInfo.meetingTypes.weekly')}</option>
                    <option value="biweekly">{t('groupInfo.meetingTypes.biweekly')}</option>
                    <option value="single">{t('groupInfo.meetingTypes.single')}</option>
                  </select>
                </div>

                {/* 3. DAG / DATUM */}
                <div className="form-group">
                  <label>{isSingleMeeting ? t('groupInfo.date') : t('groupInfo.day')} *</label>
                  {isSingleMeeting ? (
                    <input
                      type="date"
                      value={basicSettings.meetingDate || ''}
                      onChange={(e) => setBasicSettings(prev => ({...prev, meetingDate: e.target.value}))}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  ) : (
                    <select
                      value={basicSettings.meetingDay}
                      onChange={(e) => setBasicSettings(prev => ({...prev, meetingDay: e.target.value}))}
                      required
                    >
                      {weekdayOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Row 2: Tid | Min | Max */}
              <div className="form-row-three">
                {/* 4. TID */}
                <div className="form-group">
                  <label>{t('groupInfo.time')} *</label>
                  <input
                    type="time"
                    value={basicSettings.meetingTime}
                    onChange={(e) => setBasicSettings(prev => ({...prev, meetingTime: e.target.value}))}
                    required
                  />
                </div>

                {/* 5. MIN ANTAL */}
                <div className="form-group">
                  <label>{t('groupInfo.minParticipants')} *</label>
                  <input
                    type="number"
                    min="1"
                    value={basicSettings.threshold}
                    onChange={(e) => setBasicSettings(prev => ({...prev, threshold: e.target.value}))}
                    required
                  />
                </div>

                {/* 6. MAX ANTAL */}
                <div className="form-group">
                  <label>{t('groupInfo.maxParticipants')}</label>
                  <input
                    type="number"
                    min="1"
                    value={basicSettings.maxParticipants || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBasicSettings(prev => ({
                        ...prev, 
                        maxParticipants: value === '' ? null : parseInt(value)
                      }));
                    }}
                    placeholder={t('groupInfo.maxPlaceholder')}
                  />
                </div>
              </div>
              
            </div>

            {/* Separator */}
            <div style={{ margin: '25px 0', textAlign: 'center' }}>
              <span style={{ fontSize: '12px', color: '#999', fontWeight: '500' }}>
                {t('groupInfo.optionalSettings')}
              </span>
            </div>

            {/* Plats + Stad */}
            <div className="form-row">
              <div className="form-group">
                <label>{t('groupInfo.location')}</label>
                <input
                  type="text"
                  placeholder={t('groupInfo.locationPlaceholder')}
                  value={basicSettings.location}
                  onChange={(e) => setBasicSettings(prev => ({...prev, location: e.target.value}))}
                />
              </div>
              <div className="form-group">
                <label>{t('groupInfo.city')}</label>
                <input
                  type="text"
                  placeholder={t('groupInfo.cityPlaceholder')}
                  value={basicSettings.city}
                  onChange={(e) => setBasicSettings(prev => ({...prev, city: e.target.value}))}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
              <button type="submit" className="btn-save">
                {t('groupInfo.saveChanges')}
              </button>
              <div style={{ color: '#999', fontSize: '13px' }}>
                🌍 {basicSettings.city || t('groupInfo.unknownCity')} • {group.timezone || 'Europe/Stockholm'}
              </div>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default GroupInfoSection;
